import React, { useRef, useState, useCallback } from "react";
import { Play, GripVertical, ImageOff } from "lucide-react";

/**
 * DraggableCard — a positioned, rotated tile on the canvas.
 * In edit mode, drag the card body to reposition; resize via bottom-right handle.
 * In view mode, click opens the lightbox.
 */
export default function DraggableCard({ card, editable, zoom, onChange, onOpen }) {
  const dragRef = useRef(null);
  const startRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const moved = useRef(false);
  // Visitors can drag too; only edit-mode persists to storage.
  const draggable = true;

  const onPointerDown = useCallback((e) => {
    if (!draggable) return;
    e.stopPropagation();
    moved.current = false;
    startRef.current = {
      mx: e.clientX,
      my: e.clientY,
      x: card.x,
      y: card.y,
    };
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [draggable, card.x, card.y]);

  const onPointerMove = useCallback((e) => {
    if (!isDragging || !startRef.current) return;
    const dx = (e.clientX - startRef.current.mx) / zoom;
    const dy = (e.clientY - startRef.current.my) / zoom;
    if (Math.abs(dx) + Math.abs(dy) > 3) moved.current = true;
    onChange({ x: startRef.current.x + dx, y: startRef.current.y + dy });
  }, [isDragging, zoom, onChange]);

  const onPointerUp = useCallback((e) => {
    setIsDragging(false);
    startRef.current = null;
    if (e.currentTarget.releasePointerCapture) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    }
  }, []);

  const onClick = (e) => {
    if (moved.current) return;
    e.stopPropagation();
    onOpen();
  };

  // resize handle
  const onResizeDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    const start = { mx: e.clientX, my: e.clientY, w: card.width, h: card.height };
    const move = (ev) => {
      const dx = (ev.clientX - start.mx) / zoom;
      const dy = (ev.clientY - start.my) / zoom;
      onChange({
        width: Math.max(140, start.w + dx),
        height: Math.max(120, start.h + dy),
      });
    };
    const up = () => {
      setIsResizing(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const isVideo = card.type === "video";
  const poster = isVideo ? (card.thumbnail || "") : card.src;
  return (
    <div
      ref={dragRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`absolute select-none group ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
      style={{
        left: card.x,
        top: card.y,
        width: card.width,
        height: card.height,
        transform: `rotate(${card.rotation || 0}deg)`,
        transition: isDragging || isResizing ? "none" : "transform 220ms cubic-bezier(.2,.8,.2,1), box-shadow 220ms",
        zIndex: hover || isDragging ? 20 : 1,
        touchAction: "none",
        willChange: isDragging || isResizing ? "transform, left, top, width, height" : "auto",
      }}
    >
      <div
        className="relative w-full h-full bg-white card-stack overflow-hidden"
        style={{
          borderRadius: 6,
          transform: hover && !isDragging ? "translateY(-4px) scale(1.02)" : "none",
          transition: "transform 220ms cubic-bezier(.2,.8,.2,1)",
        }}
      >
        {/* media */}
        {isVideo ? (
          <div className="absolute inset-0">
            {poster ? (
              <img src={poster} alt={card.title} className="w-full h-full object-cover" draggable={false} />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[var(--ink)] via-[var(--vermillion)] to-[var(--ink)] text-white p-4 text-center">
                <Play className="w-10 h-10 mb-3 fill-white opacity-90" />
                <div className="font-display text-2xl uppercase tracking-wide leading-tight line-clamp-3">
                  {card.title}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] opacity-70 mt-2">
                  Tap to watch
                </div>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-black/0" />
            <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/55 md:backdrop-blur text-white text-[10px] uppercase tracking-[0.18em]">
              <Play className="w-3 h-3 fill-white" /> Video
            </div>
          </div>
        ) : (
          <img
            src={card.src}
            alt={card.title}
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
            loading="lazy"
            decoding="async"
            onError={(e) => {
              // graceful fallback — show a placeholder instead of the broken-image icon
              e.currentTarget.style.display = "none";
            }}
          />
        )}

        {/* image-load fallback for non-video cards */}
        {!isVideo && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[var(--paper-deep)] -z-10 p-4 text-center">
            <ImageOff className="w-7 h-7 text-ink-soft/40 mb-2" />
            <div className="font-display text-base uppercase tracking-wide text-ink-soft/70 line-clamp-3">
              {card.title}
            </div>
          </div>
        )}

        {/* footer caption that appears on hover */}
        <div
          className="absolute left-0 right-0 bottom-0 px-3 py-2.5 bg-gradient-to-t from-black/85 via-black/35 to-transparent text-white"
          style={{
            opacity: hover ? 1 : 0,
            transform: hover ? "translateY(0)" : "translateY(8px)",
            transition: "opacity 200ms, transform 200ms",
          }}
        >
          <div className="font-display text-lg leading-none truncate">{card.title}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-80 mt-1 truncate">
            {card.client || "Personal"} · {card.year}
          </div>
        </div>

        {/* category chip */}
        {card.category && (() => {
          const cat = String(card.category || "").toLowerCase().trim();
          const bg =
            cat === "video" ? "var(--vermillion)" :
            cat === "social" ? "var(--lime)" :
            cat === "motion" ? "var(--ink)" :
            "#fff";
          const fg =
            cat === "video" || cat === "motion" ? "#fff" : "#15110D";
          return (
            <div
              className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full font-medium max-w-[60%] truncate"
              style={{
                background: bg,
                color: fg,
                opacity: hover ? 1 : 0,
                transition: "opacity 200ms",
              }}
              data-testid="card-category-chip"
            >
              {card.category}
            </div>
          );
        })()}

        {/* multi-image indicator */}
        {card.gallery && card.gallery.length > 0 && (
          <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 md:backdrop-blur text-white text-[10px] font-medium tracking-wide">
            <span>+{card.gallery.length}</span>
          </div>
        )}

        {/* edit affordances */}
        {editable && (
          <>
            <div className="absolute top-2 left-2 p-1 rounded bg-white/90 border border-black/10 opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical className="w-3.5 h-3.5" />
            </div>
            <div
              onPointerDown={onResizeDown}
              className="absolute right-0 bottom-0 w-5 h-5 cursor-nwse-resize"
              style={{
                background:
                  "linear-gradient(135deg, transparent 50%, rgba(21,17,13,0.55) 50%)",
                borderBottomRightRadius: 6,
              }}
              title="Resize"
            />
          </>
        )}
      </div>
    </div>
  );
}
